import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useGetOrderByIdQuery } from "@/features/api/paymentApi";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PaymentStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const orderId =
    searchParams.get("orderId") ||
    searchParams.get("merchantOrderId");

  const [status, setStatus] = useState("PENDING");

  const {
    data: order,
    isLoading,
    isError,
    error,
  } = useGetOrderByIdQuery(orderId, {
    skip: !orderId,
    pollingInterval: status === "PENDING" ? 4000 : 0,
  });

  /* ---------- SET STATUS ---------- */
  useEffect(() => {
    if (order?.status) {
      setStatus(order.status.toUpperCase());
    }
  }, [order]);

  /* ---------- ERROR ---------- */
  useEffect(() => {
    if (isError && error) {
      toast.error(
        error?.data?.message || "Failed to fetch order"
      );
    }
  }, [isError, error]);

  /* ---------- AUTO REDIRECT ON SUCCESS ---------- */
  useEffect(() => {
    if (status === "SUCCESS" && order) {
      const slug =
        order.courseSlug || order.slug;

      if (order.productType === "course" && slug) {
        navigate(`/courses/${slug}/learn`, {
          replace: true,
        });
      }
    }
  }, [status, order, navigate]);

  /* ---------- NO ORDER ID ---------- */
  if (!orderId) {
    return (
      <Wrapper>
        <MessageCard
          title="Invalid Request"
          color="yellow"
          message="Missing order reference."
        />
      </Wrapper>
    );
  }

  /* ---------- LOADING ---------- */
  if (isLoading) {
    return (
      <Wrapper>
        <div className="flex flex-col items-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
          <p className="mt-4">Checking payment status...</p>
        </div>
      </Wrapper>
    );
  }

  /* ---------- ERROR ---------- */
  if (isError || !order) {
    return (
      <Wrapper>
        <MessageCard
          title="Error"
          color="red"
          message="Unable to fetch order details."
        />
      </Wrapper>
    );
  }

  /* ---------- UI STATES ---------- */
  if (status === "PENDING") {
    return (
      <Wrapper>
        <StatusCard
          title="Payment Pending"
          color="yellow"
          order={order}
        />
      </Wrapper>
    );
  }

  if (status === "SUCCESS") {
    return (
      <Wrapper>
        <StatusCard
          title="Payment Successful"
          color="green"
          order={order}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <StatusCard
        title="Payment Failed"
        color="red"
        order={order}
        retry
      />
    </Wrapper>
  );
};

/* ---------------- COMPONENTS ---------------- */

const Wrapper = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    {children}
  </div>
);

const StatusCard = ({ title, color, order, retry }) => {
  const navigate = useNavigate();

  const colors = {
    green: "bg-green-50 text-green-700",
    yellow: "bg-yellow-50 text-yellow-700",
    red: "bg-red-50 text-red-700",
  };

  return (
    <div
      className={`w-full max-w-lg p-8 rounded-xl shadow border ${colors[color]}`}
    >
      <h1 className="text-3xl font-bold text-center mb-6">
        {title}
      </h1>

      <div className="space-y-2 mb-6">
        <p>
          <strong>Course:</strong>{" "}
          {order.courseName || "—"}
        </p>
        <p>
          <strong>Amount:</strong> ₹{order.amount}
        </p>
        <p>
          <strong>Transaction ID:</strong>{" "}
          {order.transactionId || "—"}
        </p>
      </div>

      <div className="flex justify-center">
        {retry ? (
          <button
            onClick={() =>
              navigate(`/checkout?courseId=${order.courseId}&slug=${order.courseSlug}`)
            }
            className="bg-red-600 text-white px-6 py-3 rounded"
          >
            Retry Payment
          </button>
        ) : (
          <Link
            to="/courses"
            className="bg-blue-600 text-white px-6 py-3 rounded"
          >
            Go to Courses
          </Link>
        )}
      </div>
    </div>
  );
};

const MessageCard = ({ title, message, color }) => {
  const colors = {
    green: "bg-green-50 text-green-700",
    yellow: "bg-yellow-50 text-yellow-700",
    red: "bg-red-50 text-red-700",
  };

  return (
    <div
      className={`w-full max-w-lg p-8 rounded-xl shadow border ${colors[color]}`}
    >
      <h1 className="text-2xl font-bold text-center mb-4">
        {title}
      </h1>
      <p className="text-center">{message}</p>
    </div>
  );
};

export default PaymentStatus;